// Clases comunes del tema dopamina (manual Ecommerce White Label, cap. 04; rediseño v2, 7). Solo lectura.
import type { CSSProperties } from "react";
import {
  Bed, Cable, Check, Clock, Droplet, Feather, Flame, Gem, Gift, Hand, Heart, House, Leaf, Lightbulb, Magnet, Moon, Music, Package,
  Palette, PawPrint, Plug, Recycle, Ruler, Scissors, Shield, Shirt, Smile, Snowflake, Sparkles, Star, Sun, Thermometer, Volume2,
  WashingMachine, Waves, Wind, Zap, type LucideIcon,
} from "lucide-react";
import type { SalesContent, SectionTone } from "../types";

/** Contenedor: 20 px de margen en móvil, 40 en ordenador, máximo 1200 (04 §4). */
export const WRAP = "mx-auto w-full max-w-[75rem] px-5 lg:px-10";
/** Separación entre secciones: 56 móvil, 96 ordenador. */
export const SECTION = "py-14 lg:py-24";
export const FOCUS = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--wl-ink)";
export const H1 = "font-display text-[2.25rem] leading-[1.05] tracking-[-0.02em] text-balance lg:text-[3.75rem] [font-weight:var(--wl-display-weight)] wl-display";
export const H2 = "font-display text-[1.875rem] leading-[1.1] tracking-[-0.01em] text-balance lg:text-[2.75rem] [font-weight:var(--wl-display-weight)] wl-display";
export const EYEBROW = "text-xs font-bold uppercase tracking-[0.12em] lg:text-[0.8125rem]";
/** Botón principal (7.3): 62 px, degradado del acento, mayúsculas, sombra de color y reflejo. */
export const PRIMARY = "wl-btn " + FOCUS;
/** Botón secundario de cristal (7.2). */
export const SECONDARY = "wl-glass inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 font-bold transition-transform duration-150 active:scale-[0.97] " + FOCUS;
/** Tarjeta flotante (7.4): radio 20, sombra en capas del acento. */
export const CARD = "rounded-[20px] bg-(--wl-surface) shadow-(--wl-shadow-soft)";
/** Píldora de insignia con el degradado del acento. */
export const BADGE = "wl-grad-accent inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-[0.06em]";
/** Marcador de borrador (20.2 bis): píldora con borde discontinuo, nunca un valor inventado. */
export const PENDING = "inline-flex items-center rounded-[6px] border border-dashed border-(--wl-ink-muted) px-2 py-0.5 text-[0.8125rem] text-(--wl-ink-muted)";
export const LINK = "font-semibold underline decoration-2 underline-offset-4 " + FOCUS;

const ICONS: Record<string, LucideIcon> = {
  sparkles: Sparkles, zap: Zap, hand: Hand, clock: Clock, droplet: Droplet, moon: Moon, sun: Sun, heart: Heart, gift: Gift, package: Package,
  ruler: Ruler, leaf: Leaf, wind: Wind, music: Music, feather: Feather, home: House, paw: PawPrint, shirt: Shirt, lightbulb: Lightbulb,
  magnet: Magnet, plug: Plug, scissors: Scissors, thermometer: Thermometer, smile: Smile, recycle: Recycle, washing: WashingMachine, bed: Bed,
  palette: Palette, volume: Volume2, gem: Gem, cable: Cable, waves: Waves, snowflake: Snowflake, flame: Flame, shield: Shield, star: Star, check: Check,
};

export function iconOf(name: string): LucideIcon {
  return ICONS[name] ?? Check;
}

/** Las variables de la paleta, los degradados y la pareja de letras, en la raíz del tema. */
export function tokenStyle(sales: SalesContent): CSSProperties {
  const t = sales.tokens;
  return {
    "--wl-accent": t.accent,
    "--wl-on-accent": t.onAccent,
    "--wl-accent-text": t.accentText,
    "--wl-accent-soft": t.accentSoft,
    "--wl-accent-press": t.accentPress,
    "--wl-accent-on-surface": t.accentOnSurface,
    "--wl-pair": t.pair,
    "--wl-photo-bg": t.photoBg,
    "--wl-ink": t.ink,
    "--wl-ink-muted": t.inkMuted,
    "--wl-surface": t.surface,
    "--wl-line": t.line,
    "--wl-star": t.star,
    "--wl-urgency": t.urgency,
    "--wl-urgency-bg": t.urgencyBg,
    "--wl-success": t.success,
    "--wl-grad-accent": t.gradAccent,
    "--wl-on-grad-accent": t.onGradAccent,
    "--wl-grad-bar": t.gradBar,
    "--wl-on-grad-bar": t.onGradBar,
    "--wl-grad-soft": t.gradSoft,
    "--wl-grad-dark": t.gradDark,
    "--wl-on-dark": t.onDark,
    "--wl-accent-on-dark": t.accentOnDark,
    "--wl-grad-text": t.gradText,
    "--wl-shadow-accent": t.shadowAccent,
    "--wl-shadow-soft": t.shadowSoft,
    "--wl-glass": t.glass,
    "--wl-glass-solid": t.glassSolid,
    "--wl-glass-border": t.glassBorder,
    "--wl-radius": String(sales.radius) + "px",
    "--wl-btn-radius": String(sales.buttonRadius) + "px",
    "--wl-display-weight": String(sales.displayWeight),
    "--wl-display-case": sales.displayUppercase ? "uppercase" : "none",
  } as CSSProperties;
}

/** El fondo de cada sección (7.1): liso, manchas de la marca, bloque oscuro o el degradado del acento. */
export const TONES: Record<SectionTone, string> = {
  surface: "bg-(--wl-surface) text-(--wl-ink)",
  soft: "wl-soft text-(--wl-ink)",
  dark: "wl-dark",
  accent: "wl-grad-accent",
};

const PRICE = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" });

export function euros(value: number): string {
  return PRICE.format(value);
}

/** Precio de N unidades con los packs (el mayor que quepa, y así): 2 = el pack de 2; 4 = pack de 3 + 1. */
export function priceForUnits(units: number, packs: SalesContent["packs"], unitPrice: number): number {
  let left = units;
  let total = 0;
  const sorted = [...packs].sort((a, b) => b.units - a.units);
  while (left > 0) {
    const pack = sorted.find((item) => item.units <= left);
    if (pack === undefined) {
      total += unitPrice * left;
      break;
    }
    total += pack.price;
    left -= pack.units;
  }
  return Math.round(total * 100) / 100;
}
